import React, { useCallback, useMemo, useState } from 'react';
import { Card, Space, Tag, Typography, Button } from 'antd';
import { normalizeBase } from '@digiwedge/ui-health-panels';

export interface K8sSyncStatusPanelProps {
  base: string;
}

type Status = 'idle' | 'ok' | 'fail';

export default function K8sSyncStatusPanel({ base }: K8sSyncStatusPanelProps): React.ReactElement {
  const { Text } = Typography;
  const url = useMemo(() => (base ? `${normalizeBase(base)}/health/ready` : ''), [base]);
  const [status, setStatus] = useState<Status>('idle');
  const [headers, setHeaders] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(false);

  const refresh = useCallback(async () => {
    if (!url) return;
    setLoading(true);
    try {
      const res = await fetch(url, { credentials: 'include' });
      const hdrs: Record<string, string> = {};
      ['x-app-image', 'x-app-digest', 'x-app-release', 'etag'].forEach((h) => {
        const v = res.headers.get(h);
        if (v) hdrs[h] = v;
      });
      setHeaders(hdrs);
      setStatus(res.ok ? 'ok' : 'fail');
    } catch {
      setStatus('fail');
      setHeaders({});
    } finally {
      setLoading(false);
    }
  }, [url]);

  return (
    <Card
      title="Kubernetes Sync Status"
      extra={
        <Button size="small" onClick={() => void refresh()} loading={loading}>
          Refresh
        </Button>
      }
    >
      <Space direction="vertical" style={{ width: '100%' }}>
        <Space>
          <Text strong>Endpoint</Text>
          <Text code>{url || 'not set'}</Text>
          <Tag color={status === 'ok' ? 'green' : status === 'fail' ? 'red' : 'default'}>
            {status.toUpperCase()}
          </Tag>
        </Space>
        <Space size="small" wrap>
          {Object.entries(headers).length > 0 ? (
            Object.entries(headers).map(([k, v]) => (
              <Tag key={k} color={k === 'etag' ? 'blue' : 'purple'}>
                {k}: {v}
              </Tag>
            ))
          ) : (
            <Text type="secondary">No status headers found.</Text>
          )}
        </Space>
      </Space>
    </Card>
  );
}
